import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Paper, Toolbar, Typography, Stack, TextField, Button, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";

export default function StudentActivityDetailsUpload() {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [acyear, setAcyear] = useState({ acyear_id: "", acyear_name: "" });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    activity_category_id: "",
    activity_type_id: "",
    acyear_id: "",
    student_rollno: "",
    student_id: "",
    main_activity: "",
    noa: "",
    proof: null,
    proofPreview: null,
    showPreview: false,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [editForm, setEditForm] = useState({
    activity_category_id: "",
    activity_type_id: "",
    main_activity: "",
    noa: "",
    proof: null,
    proofPreview: null,
  });
  const backendUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : {};
    } catch (err) {
      return {};
    }
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!user.client_id || !user.college_id) return;
    axios
      .get(`${API_BASE_URL}/api/activitycategories/${user.client_id}/${user.college_id}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setCategories(res.data?.data || []));
    axios
      .get(`${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${user.college_id}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const years = Array.isArray(res.data?.data) ? res.data.data : [];
        const selectedYear = years.find((year) => Number(year.is_active) === 1) || years[0] || {};
        setAcyear(selectedYear);
        setForm((prev) => ({
          ...prev,
          acyear_id: selectedYear.acyear_id || "",
        }));
      })
      .catch(() => {
        setAcyear({ acyear_id: "", academic_year: "" });
      });
  }, [API_BASE_URL, user.client_id, user.college_id]);

  useEffect(() => {
    if (!form.activity_category_id) return;
    axios
      .get(
        `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
          Number(user.college_id) || 1
        }/${form.activity_category_id}`,
        { headers: getAuthHeaders() }
      )
      .then((res) => setTypes(res.data?.data || []));
  }, [form.activity_category_id, API_BASE_URL, user.client_id, user.college_id]);

  // Fetch student by email
  useEffect(() => {
    if (!user.user_email) return;
    axios
      .get(
        `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}?college_id=${encodeURIComponent(user.college_id || "")}`,
        { headers: getAuthHeaders() }
      )
      .then((res) => {
        const data = res.data?.data || {};
        setForm((prev) => ({
          ...prev,
          student_id: data.student_id || "",
          student_rollno: data.student_rollno || "",
        }));
      });
  }, [API_BASE_URL, user.client_id, user.user_email]);

  // Fetch activities
  const fetchActivities = useCallback(() => {
    if (!user.client_id || !form.student_id) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/studentactivities/${user.client_id}/${form.student_id}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setActivities(res.data?.data || []))
      .finally(() => setLoading(false));
  }, [API_BASE_URL, user.client_id, form.student_id]);

  useEffect(() => {
    if (form.student_id) fetchActivities();
  }, [form.student_id, fetchActivities]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/(jpg|jpeg|png)$/i.test(file.name) || file.size > 2 * 1024 * 1024) {
      showSnackbar("Only JPG/PNG files under 2MB allowed", "error");
      return;
    }
    setForm((prev) => ({ ...prev, proof: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setForm((prev) => ({ ...prev, proofPreview: ev.target.result }));
    reader.readAsDataURL(file);
    showSnackbar(`File selected: ${file.name}`, "info");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = [
      "activity_category_id",
      "activity_type_id",
      "student_rollno",
      "student_id",
      "acyear_id",
      "main_activity",
      "noa",
      "proof",
    ];
    const missing = required.filter((k) => !form[k]);
    if (missing.length > 0) {
      showSnackbar("Please fill all required fields.", "error");
      return;
    }
    const fd = new FormData();
    fd.append("client_id", user.client_id);
    fd.append("college_id", user.college_id);
    fd.append("acyear_id", form.acyear_id || acyear.acyear_id);
    fd.append("student_rollno", form.student_rollno);
    fd.append("student_id", form.student_id);
    fd.append("activity_category_id", form.activity_category_id);
    fd.append("activity_type_id", form.activity_type_id);
    fd.append("main_activity", form.main_activity);
    fd.append("noa", form.noa);
    fd.append("proof", form.proof);
    try {
      await axios.post(`${API_BASE_URL}/api/studentactivities`, fd, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      showSnackbar("Activity saved successfully", "success");
      setForm((prev) => ({
        ...prev,
        main_activity: "",
        noa: "",
        proof: null,
        proofPreview: null,
        activity_category_id: "",
        activity_type_id: "",
      }));
      fetchActivities();
    } catch (err) {
      showSnackbar("Failed to save activity", "error");
    }
  };

  // DataGrid columns
  const columns = [
    {
      field: "serial",
      headerName: "Serial No",
      flex: 0.5,
      minWidth: 80,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "activity_category",
      headerName: "Category",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.activity_category?.activity_category,
    },
    {
      field: "activity_type",
      headerName: "Type",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.activity_type?.activity_type,
    },
    { field: "main_activity", headerName: "Main Activity", flex: 1, minWidth: 120 },
    { field: "noa", headerName: "Nature of Activity", flex: 1, minWidth: 120 },
    {
      field: "proof",
      headerName: "Proof",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (params.value ? params.value.split("/").pop() : "-"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{
            height: 48,
            minWidth: 120,
            fontWeight: "bold",
            fontSize: "1rem",
            borderRadius: 2,
            color: "#fff",
            boxShadow: 2,
          }}
          onClick={() => {
            setEditActivity(params.row);
            setEditForm({
              activity_category_id: params.row.activity_category?.activity_category_id || "",
              activity_type_id: params.row.activity_type?.activity_type_id || "",
              main_activity: params.row.main_activity || "",
              noa: params.row.noa || "",
              proof: null,
              proofPreview: null,
            });
            setEditOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const handleEditSave = async () => {
    if (!editActivity) return;
    const fd = new FormData();
    fd.append("client_id", user.client_id);
    fd.append("college_id", user.college_id);
    fd.append("acyear_id", editActivity.acyear_id || form.acyear_id || acyear.acyear_id);
    fd.append("student_rollno", editActivity.student_rollno);
    fd.append("student_id", editActivity.student_id);
    fd.append("activity_category_id", editForm.activity_category_id);
    fd.append("activity_type_id", editForm.activity_type_id);
    fd.append("main_activity", editForm.main_activity);
    fd.append("noa", editForm.noa);
    if (editForm.proof) fd.append("proof", editForm.proof);
    try {
      await axios.put(
        `${API_BASE_URL}/api/studentactivities/${user.client_id}/${editActivity.student_activity_id}`,
        fd,
        {
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        }
      );
      showSnackbar("Activity updated successfully", "success");
      setEditOpen(false);
      fetchActivities();
    } catch (err) {
      showSnackbar("Failed to update activity", "error");
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/(jpg|jpeg|png)$/i.test(file.name) || file.size > 2 * 1024 * 1024) {
      showSnackbar("Only JPG/PNG files under 2MB allowed", "error");
      return;
    }
    setEditForm((prev) => ({ ...prev, proof: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setEditForm((prev) => ({ ...prev, proofPreview: ev.target.result }));
    reader.readAsDataURL(file);
    showSnackbar(`File selected: ${file.name}`, "info");
  };

  useEffect(() => {
    if (editOpen && editForm.activity_category_id) {
      axios
        .get(
          `${API_BASE_URL}/api/activitytypes/${Number(user.client_id) || 1}/${
            Number(user.college_id) || 1
          }/${editForm.activity_category_id}`,
          { headers: getAuthHeaders() }
        )
        .then((res) => {
          setTypes(res.data?.data || []);
        });
    }
  }, [editOpen, editForm.activity_category_id, API_BASE_URL, user.client_id, user.college_id]);

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            STUDENT ACTIVITY DETAILS UPLOAD
          </Typography>
        </Toolbar>
        <Box p={2}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
              <TextField
                select
                margin="normal"
                label="Activity Category"
                name="activity_category_id"
                value={form.activity_category_id}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.activity_category_id} value={cat.activity_category_id}>
                    {cat.activity_category}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                margin="normal"
                label="Activity Type"
                name="activity_type_id"
                value={form.activity_type_id}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                required
                disabled={!form.activity_category_id}
              >
                <option value="">Select Type</option>
                {types.map((type) => (
                  <option key={type.activity_type_id} value={type.activity_type_id}>
                    {type.activity_type}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Main Activity"
                name="main_activity"
                value={form.main_activity}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Nature of Activity"
                name="noa"
                value={form.noa}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                multiline
                minRows={2}
              />
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{
                  color: "#fff",
                  fontWeight: 500,
                  minHeight: 48,
                  borderRadius: 2,
                  fontSize: "1rem",
                }}
              >
                UPLOAD PROOF
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                disabled={!form.proofPreview}
                onClick={() => setPreviewOpen(true)}
                sx={{
                  color: "#fff",
                  backgroundColor: "#1976d2",
                  borderColor: "#1976d2",
                  fontWeight: 500,
                  letterSpacing: 1,
                  minHeight: 48,
                  borderRadius: 2,
                  fontSize: "1rem",
                  ml: 2,
                }}
              >
                PREVIEW
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ height: 56, minWidth: 120, fontWeight: "bold", fontSize: 16, color: "#fff" }}
              >
                Save Activity
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
      {/* Activities Grid */}
      <Box mt={3}>
        <DataGrid
          rows={activities}
          columns={columns}
          autoHeight
          loading={loading}
          getRowId={(row) => row.student_activity_id}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          disableRowSelectionOnClick
        />
      </Box>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm">
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="subtitle2">Image Preview:</Typography>
          {form.proofPreview && (
            <img
              src={form.proofPreview}
              alt="Preview"
              style={{
                maxWidth: 300,
                borderRadius: 8,
                marginTop: 10,
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Student Activity</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              {/* First row: Category, Type */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  margin="normal"
                  label="Activity Category"
                  name="activity_category_id"
                  value={editForm.activity_category_id}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, activity_category_id: e.target.value }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.activity_category_id} value={cat.activity_category_id}>
                      {cat.activity_category}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  margin="normal"
                  label="Activity Type"
                  name="activity_type_id"
                  value={editForm.activity_type_id}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, activity_type_id: e.target.value }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true }}
                  required
                  disabled={!editForm.activity_category_id}
                >
                  <option value="">Select Type</option>
                  {types.map((type) => (
                    <option key={type.activity_type_id} value={type.activity_type_id}>
                      {type.activity_type}
                    </option>
                  ))}
                </TextField>
              </Grid>
              {/* Second row: Main Activity, Nature of Activity */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Main Activity"
                  name="main_activity"
                  value={editForm.main_activity}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, main_activity: e.target.value }))
                  }
                  fullWidth
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nature of Activity"
                  name="noa"
                  value={editForm.noa}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, noa: e.target.value }))}
                  fullWidth
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{
                color: "#fff",
                fontWeight: 500,
                minHeight: 48,
                borderRadius: 2,
                fontSize: "1rem",
              }}
            >
              UPLOAD PROOF
              <input
                type="file"
                accept="image/jpeg,image/png"
                hidden
                onChange={handleEditFileChange}
              />
            </Button>
            {editForm.proof && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                File selected: {editForm.proof.name}
              </Typography>
            )}
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              disabled={!editForm.proofPreview}
              onClick={() => setEditForm((prev) => ({ ...prev, showPreview: !prev.showPreview }))}
              sx={{ minHeight: 48, borderRadius: 2, fontSize: "1rem", ml: 2 }}
            >
              Preview
            </Button>
            {editForm.showPreview && editForm.proofPreview && (
              <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                <Typography variant="subtitle2">Image Preview:</Typography>
                <img
                  src={editForm.proofPreview}
                  alt="Preview"
                  style={{
                    maxWidth: 300,
                    borderRadius: 8,
                    marginTop: 10,
                    boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="primary">
            Close
          </Button>
          <Button onClick={handleEditSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
